import React, { useState, useEffect, useCallback } from 'react';
import { getBusinesses, createBusiness, updateBusiness } from '../services/api';

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
}

function NewBusinessModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', code: '', adminName: '', adminEmail: '', adminPhone: '', adminPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name || !form.adminName || !form.adminEmail || !form.adminPhone || !form.adminPassword) {
      setError('Completa el nombre del negocio y todos los datos del admin.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createBusiness(form);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear negocio.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nuevo Negocio</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}
          <div className="modal-form-group">
            <label>Nombre del negocio</label>
            <input className="text-input" value={form.name} onChange={set('name')} disabled={saving} />
          </div>
          <div className="modal-form-group">
            <label>Código (opcional — se genera solo si lo dejas vacío)</label>
            <input className="text-input" value={form.code} onChange={set('code')} disabled={saving}
              placeholder="Ej: NEGOCIO1" style={{ textTransform: 'uppercase' }} />
          </div>
          <hr className="modal-divider" />
          <p className="modal-label" style={{ marginBottom: 12 }}>Admin del negocio</p>
          <div className="modal-form-group">
            <label>Nombre</label>
            <input className="text-input" value={form.adminName} onChange={set('adminName')} disabled={saving} />
          </div>
          <div className="modal-form-group">
            <label>Correo</label>
            <input className="text-input" type="email" value={form.adminEmail} onChange={set('adminEmail')} disabled={saving} />
          </div>
          <div className="modal-form-group">
            <label>Teléfono</label>
            <input className="text-input" value={form.adminPhone} onChange={set('adminPhone')} disabled={saving} />
          </div>
          <div className="modal-form-group">
            <label>Contraseña</label>
            <input className="text-input" type="text" value={form.adminPassword} onChange={set('adminPassword')} disabled={saving}
              placeholder="Con esto entra al panel" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-cancel-btn" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="modal-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Creando...' : 'Crear negocio'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Businesses() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getBusinesses();
      setBusinesses(data.businesses || []);
    } catch (err) {
      setError('Error al cargar negocios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  const copyCode = (b) => {
    navigator.clipboard?.writeText(b.code);
    setCopiedId(b.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const regenerate = async (b) => {
    if (!window.confirm(`¿Regenerar el código de "${b.name}"? Los vendedores tendrán que usar el nuevo código para registrarse (los ya registrados no se afectan).`)) return;
    setBusyId(b.id);
    try { await updateBusiness(b.id, { regenerateCode: true }); await fetchBusinesses(); }
    catch { setError('No se pudo regenerar el código.'); }
    finally { setBusyId(null); }
  };

  const toggleActive = async (b) => {
    setBusyId(b.id);
    try { await updateBusiness(b.id, { active: !b.active }); await fetchBusinesses(); }
    catch (err) { setError(err.response?.data?.error || 'No se pudo actualizar.'); }
    finally { setBusyId(null); }
  };

  return (
    <>
      <div className="filter-bar">
        <p className="section-hint">Cada negocio tiene un código único. Los vendedores lo usan al registrarse en la app para quedar ligados a ese negocio.</p>
        <button className="primary-button" onClick={() => setShowNew(true)}>+ Nuevo Negocio</button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="table-container">
        {loading && businesses.length === 0 ? (
          <div className="loading-state"><div className="spinner"></div><p>Cargando negocios...</p></div>
        ) : (
          <table className="portings-table">
            <thead>
              <tr>
                <th>Negocio</th>
                <th>Código</th>
                <th>Admin(s)</th>
                <th style={{ textAlign: 'center' }}>Vendedores</th>
                <th style={{ textAlign: 'center' }}>Estado</th>
                <th>Alta</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {businesses.map((b) => (
                <tr key={b.id} className={!b.active ? 'row-inactive' : ''}>
                  <td>
                    {b.name}
                    {b.isRoot && <span className="root-tag"> principal</span>}
                  </td>
                  <td>
                    <span className="code-chip" onClick={() => copyCode(b)} title="Clic para copiar">
                      {b.code} {copiedId === b.id ? '✓' : '⧉'}
                    </span>
                  </td>
                  <td>
                    {b.admins.map((a) => (
                      <div key={a.id} className="admin-line">{a.name} <span className="admin-email">{a.email}</span></div>
                    ))}
                  </td>
                  <td style={{ textAlign: 'center' }}>{b.sellersCount}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`status-badge ${b.active ? 'badge-green' : 'badge-red'}`}>
                      {b.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="cell-date">{formatDate(b.createdAt)}</td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button className="small-btn" disabled={busyId === b.id} onClick={() => regenerate(b)}>Regenerar código</button>
                    {!b.isRoot && (
                      <button
                        className={b.active ? 'small-btn danger' : 'small-btn success'}
                        style={{ marginLeft: 6 }}
                        disabled={busyId === b.id}
                        onClick={() => toggleActive(b)}
                      >
                        {b.active ? 'Desactivar' : 'Activar'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <NewBusinessModal onClose={() => setShowNew(false)} onSaved={async () => { setShowNew(false); await fetchBusinesses(); }} />}
    </>
  );
}

export default Businesses;
